import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getTopicTypes, getTopics, createTopic } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { BookOpen, Save, AlertCircle, HelpCircle } from 'lucide-react';

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface FormErrors {
  name?: string;
  slug?: string;
  topicTypeId?: string;
  submit?: string;
}

export default function TopicsNewPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Auth check
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add topics.</p>
        </div>
      </div>
    );
  }

  // Load topic types and topics for dropdowns
  const { data: topicTypes = [] } = useQuery(getTopicTypes);
  const { data: topics = [] } = useQuery(getTopics);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    topicTypeId: '',
    description: '',
    content: '',
    parentTopicId: null as string | null
  });

  const handleChange = (field: string, value: string | null) => {
    setFormData(f => ({ ...f, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(e => ({ ...e, [field]: undefined }));
    }
  };

  const handleNameChange = (name: string) => {
    setFormData(f => ({
      ...f,
      name,
      slug: f.slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    }));
    if (errors.name) {
      setErrors(e => ({ ...e, name: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug must be lowercase letters, numbers, and dashes only';
    }

    if (!formData.topicTypeId) {
      newErrors.topicTypeId = 'Topic Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createTopic({
        ...formData,
        parentTopicId: formData.parentTopicId || undefined
      });
      navigate('/admin/topics');
    } catch (error: any) {
      console.error('Error creating topic:', error);
      setErrors({ submit: error.message || 'Failed to create topic. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/topics');
  };

  return (
    <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 pl-6 pt-4">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-content-text">Add Topic</h1>
                    <p className="text-content-text-muted">Create a new Community knowledge topic</p>
                  </div>
                </div>
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-destructive flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {errors.submit}
                  </p>
                </div>
              )}

              <form id="topic-form" onSubmit={handleSubmit}>
                {/* Primary Input Fields - 2 column grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Name *</h3>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.name ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., Prayer, Charity, Pilgrimage"
                      required
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Topic name</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Slug *</h3>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.slug ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., Prayer, Charity, Pilgrimage"
                      required
                    />
                    {errors.slug && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.slug}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">URL-friendly identifier (auto-generated)</p>
                  </div>
                </div>

                {/* Description Field - Full width */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-content-text mb-4">Description</h3>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Brief description..."
                  />
                  <p className="text-sm text-content-text-muted mt-2">{formData.description.length}/500 characters</p>
                </div>

                {/* Content Field - Full width */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-content-text mb-4">Content / Guidance</h3>
                  <textarea
                    value={formData.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Detailed Community guidance and content..."
                  />
                  <p className="text-sm text-content-text-muted mt-2">{formData.content.length}/5000 characters</p>
                </div>

                {/* Topic Type and Parent Topic - 2 column grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Topic Type *</h3>
                    <select
                      value={formData.topicTypeId}
                      onChange={(e) => handleChange('topicTypeId', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border ${
                        errors.topicTypeId ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      required
                    >
                      <option value="">Select a topic type</option>
                      {(topicTypes as any[]).map((type: any) => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                    {errors.topicTypeId && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.topicTypeId}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Category for this topic</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Parent Topic</h3>
                    <select
                      value={formData.parentTopicId || ''}
                      onChange={(e) => handleChange('parentTopicId', e.target.value || null)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border"
                    >
                      <option value="">None (Root Topic)</option>
                      {(topics as any[]).map((topic: any) => (
                        <option key={topic.id} value={topic.id}>{topic.name}</option>
                      ))}
                    </select>
                    <p className="text-sm text-content-text-muted mt-2">Optional hierarchical parent</p>
                  </div>
                </div>
              </form>
            </div>

            {/* Right Sidebar */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="topic-form"
                    disabled={isLoading || !formData.name.trim() || !formData.slug.trim() || !formData.topicTypeId}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Help Section */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Help
                </h3>

                <div className="space-y-3 text-sm text-content-text-muted">
                  <p>• Name, slug, and topic type are required</p>
                  <p>• Slug is auto-generated from name</p>
                  <p>• Content field for detailed Community guidance</p>
                  <p>• Parent topic is optional for hierarchical structure</p>
                  <p>• Examples: Prayer → Jummah → Khutbah Preparation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



